import { useMemo, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans } from 'react-i18next';
import { Product, useCountriesFromKeys } from '@app/api';
import { toEuros } from 'i18n';
import { Chip, IconChip } from 'components';
import classNames from 'classnames';
import {
  computeIncentive,
  getUnitProductPrice,
  sumProducts,
} from 'lib/priceComputation';

const useStyles = createUseStyles<string, {}, Theme>((theme: Theme) => ({
  container: {},
  mainLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase / 2,
    flexWrap: 'wrap',
  },
  countriesRow: {
    flexBasis: '100%',
    display: 'flex',
    gap: theme.marginBase / 2,
    marginLeft: theme.marginBase,
  },
  arrow: {
    fontSize: theme.iconSize.large,
    color: theme.colors.blue,
    transition: 'transform 300ms ',
  },
  arrowOpen: {
    transform: 'rotate(-90deg)',
  },
  title: {
    ...theme.fonts.h5,
  },
  total: {
    ...theme.fonts.body,
    flex: 1,
    textAlign: 'right',
  },
  totalStrong: {
    ...theme.fonts.h5,
  },
  animatedContent: {
    transition: 'max-height 300ms',
    maxHeight: 0,
    overflow: 'hidden',
  },
  animatedContentOpen: {
    maxHeight: 1000,
  },
  products: {
    padding: theme.marginBase * 2,
    borderRadius: theme.borderRadius.std,
    backgroundColor: theme.colors.white,
    marginTop: theme.marginBase,

    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase,
  },
  productLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
  },
  productName: {
    ...theme.fonts.caption,
    flex: 1,
  },
  productPrice: {
    ...theme.fonts.caption,
  },
  strong: {
    ...theme.fonts.label,
  },
}));

interface Props {
  product?: Product;
  products?: Product[];
  duration?: number;
  title?: string;
  numberOfParticipants?: number;
  countryKeys?: string[];
  displayPerParticipantChip?: boolean;
  displayNumberOfParticipant?: boolean;
  displayCountriesInSeparateRow?: boolean;
  defaultOpened?: boolean;
}

export const ProductListSection = ({
  title,
  products: productsProps,
  product,
  duration,
  numberOfParticipants,
  countryKeys,
  displayPerParticipantChip,
  displayNumberOfParticipant,
  displayCountriesInSeparateRow,
  defaultOpened = false,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const countries = useCountriesFromKeys(countryKeys);

  const openable = !!productsProps;
  const [open, setOpen] = useState(defaultOpened);

  const products = useMemo(() => {
    if (productsProps) {
      return productsProps;
    }
    if (product) {
      return [product];
    }
    return [];
  }, [productsProps, product]);

  const incentive = useMemo(
    () => computeIncentive(products, duration),
    [products],
  );

  const total = useMemo(() => {
    return sumProducts(
      products,
      numberOfParticipants || 1,
      duration,
      incentive,
    );
  }, [products, duration, incentive, numberOfParticipants]);

  const components = useMemo(
    () => ({
      strong: <strong className={classes.strong} />,
    }),
    [classes],
  );

  if (products.length === 0) {
    return null;
  }

  return (
    <div className={classes.container}>
      <div
        className={classes.mainLine}
        role={openable ? 'button' : ''}
        onClick={() => setOpen((v) => !v)}
      >
        {openable && (
          <i
            className={classNames('ri-arrow-right-s-line', classes.arrow, {
              [classes.arrowOpen]: open,
            })}
          />
        )}
        <div className={classes.title}>{title || product?.title}</div>
        {!displayCountriesInSeparateRow &&
          countries.map((country) => (
            <Chip color="green" key={country.key}>
              {country.text}
            </Chip>
          ))}
        {!!numberOfParticipants && displayNumberOfParticipant && (
          <IconChip icon="ri-user-line">{numberOfParticipants}</IconChip>
        )}
        <div className={classes.total}>
          {!total || numberOfParticipants ? (
            <span className={classes.totalStrong}>{toEuros(total, true)}</span>
          ) : displayPerParticipantChip ? (
            <>
              <Trans
                i18nKey="billing.products.detail.price"
                values={{ count: total }}
                components={{
                  strong: <span className={classes.totalStrong} />,
                }}
              />
              {' / '}
              <IconChip icon="ri-user-line" />
            </>
          ) : (
            <Trans
              i18nKey="billing.products.detail.pricePerParticipant"
              values={{ count: total }}
              components={{
                strong: <span className={classes.totalStrong} />,
              }}
            />
          )}
        </div>
        {displayCountriesInSeparateRow && (
          <div className={classes.countriesRow}>
            {countries.map((country) => (
              <Chip color="green" key={country.key}>
                {country.text}
              </Chip>
            ))}
          </div>
        )}
      </div>
      {openable && (
        <div
          className={classNames(classes.animatedContent, {
            [classes.animatedContentOpen]: open,
          })}
        >
          <div className={classes.products}>
            {products.map((product) => (
              <div className={classes.productLine} key={product.id}>
                <div className={classes.productName}>{product.title}</div>
                <div className={classes.productPrice}>
                  {product.pricePerParticipant ? (
                    <Trans
                      i18nKey="billing.products.detail.pricePerParticipant"
                      values={{
                        count: getUnitProductPrice(
                          product,
                          duration,
                          incentive,
                        ),
                      }}
                      components={components}
                    />
                  ) : (
                    <Trans
                      i18nKey="billing.products.detail.price"
                      values={{
                        count: getUnitProductPrice(
                          product,
                          duration,
                          incentive,
                        ),
                      }}
                      components={components}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
